import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';

type AuthContextType = {
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  checkSessionValidity: () => boolean;
  refreshTokens: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

// Token refresh threshold in milliseconds (5 minutes)
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000;

// Check if token is approaching expiration based on JWT payload
const isTokenNearExpiry = (token: string): boolean => {
  try {
    // Split the token and get the payload
    const base64Url = token.split('.')[1];
    if (!base64Url) return true;
    
    // Convert base64url to base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode and parse JSON
    const payloadJson = JSON.parse(atob(base64));
    
    // Get expiration time
    if (!payloadJson.exp) return true;
    
    const expTime = payloadJson.exp * 1000; // convert to milliseconds
    const currentTime = Date.now();
    
    // Return true if token expires within threshold
    return (expTime - currentTime) < TOKEN_REFRESH_THRESHOLD;
  } catch (error) {
    console.error("Error parsing token:", error);
    return true; // Assume token needs refresh if we can't parse it
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Try to initialize from sessionStorage to maintain state across page refreshes
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const storedAuth = sessionStorage.getItem('isAuthenticated');
    return storedAuth === 'true';
  });

  // Update sessionStorage when authentication state changes
  useEffect(() => {
    sessionStorage.setItem('isAuthenticated', isAuthenticated.toString());
  }, [isAuthenticated]);

  // Check if the current session is valid by examining tokens
  const checkSessionValidity = (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    // Return false if token is expired or near expiration
    return !isTokenNearExpiry(token);
  };

  // Function to refresh tokens using MSAL
  const refreshTokens = async (): Promise<boolean> => {
    // This is just a placeholder - actual implementation is in setupAxiosResponseInterceptor
    // which handles token refresh automatically when needed
    return true;
  };

  // Context value with authentication state and helper functions
  const contextValue: AuthContextType = {
    isAuthenticated,
    setIsAuthenticated,
    checkSessionValidity,
    refreshTokens
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext); 
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

